// Finance > Financial KPI Metrics. Every row has 12 months: index 0 = September ... 11 = August.
// Values come from the API already computed; null = not available yet (future / not closed / no snapshot).

export type KpiUnit = "currency" | "percent"
export type KpiSource = "actual" | "live" | "snapshot"

export interface KpiRow {
    key: string
    label: string
    depth: number            // 0 = group / total row, 1 = child, 2 = grandchild
    position: string | null
    owner: string | null
    unit: KpiUnit
    target: number | null
    months: (number | null)[]
    sources?: (KpiSource | null)[]
    ytd: number | null
    pct: number | null
}

export interface KpiGroup {
    key: string
    label: string
    rows: KpiRow[]
}

export interface GrossMarginKpis {
    tabs: KpiGroup[]
    total: KpiRow
    // Total row followed by its region rows
    totalRows?: KpiRow[]
}

export interface KpiSnapshot {
    fiscalPeriod: number
    savedBy: string | null
    savedAt: string
}

export interface KpiSnapshotStatus {
    currentPeriod: number
    snapshots: KpiSnapshot[]
}

export interface FinancialKpiData {
    revenue: KpiRow[] | null
    clientRevenue: KpiRow[] | null
    grossMargin: GrossMarginKpis | null
    wih: KpiRow[] | null
    funnel: KpiGroup[] | null
    other: KpiRow[] | null
    snapshots: KpiSnapshotStatus | null
}

export type FinancialKpiSection = keyof FinancialKpiData

export const EMPTY_FINANCIAL_KPI_DATA: FinancialKpiData = {
    revenue: null, clientRevenue: null, grossMargin: null, wih: null, funnel: null, other: null, snapshots: null,
}

// Section -> API path under /financial-kpi
export const FINANCIAL_KPI_PATHS: Record<FinancialKpiSection, string> = {
    revenue: "revenue",
    clientRevenue: "client-revenue",
    grossMargin: "gross-margin",
    wih: "wih",
    funnel: "funnel",
    other: "other",
    snapshots: "snapshots",
}

// Drill-down modal: what makes up one month / YTD value (GET /financial-kpi/detail)
export type KpiDetailColumnType = "text" | "currency" | "percent" | "number" | "date"

export interface KpiDetailColumn {
    key: string
    label: string
    type: KpiDetailColumnType
}

export interface KpiDetail {
    note?: string
    columns: KpiDetailColumn[]
    rows: Record<string, unknown>[]
    total?: Record<string, unknown>
}
